import React, { Component, Fragment } from 'react';
import {
    DataPaper,
    DataViewCellTextColor,
    DataViewColumn, DataViewPaged,
    DataViewCellFormat,
    cloneWith,
    fromCamelCaseToHumanCase,
    ucFirst,
    Container,
    isBlank,
    sorting,
    textFilter,
    DataFormResult,
    DataView
} from "@ivorobioff/shared";
import moment from "moment";
import { TransactionService } from "../../services/TransactionService";
import { Transaction } from "../../models/Transaction";
import { createStyles, Divider, Popover, Typography, Theme, withStyles, Select, MenuItem, Box, Grid } from '@material-ui/core';
import { tap } from 'rxjs/operators';
import { Alert } from '@material-ui/lab';
import User from '../../models/User';
import { UserService } from '../../services/UserService';
import AccountService from '../../services/AccountService';
import Account from '../../models/Account';

const styles = (theme: Theme) => createStyles({
    notePopover: {
        padding: theme.spacing(2),
        paddingTop: theme.spacing(2),
        minWidth: 150,
        maxWidth: 400
    },

    noteTitle: {
        marginBottom: theme.spacing(0.5)
    },

    noteContent: {
        paddingTop: theme.spacing(1)
    }
});

export interface TransactionsPageProps {
    container: Container;
    classes: { [name: string]: string };
}

interface TransactionsPageState {
    data: Transaction[];
    user?: User;
    accounts: Account[];
    note?: {
        open: boolean;
        content?: string;
        anchor: HTMLElement;
    },
    repaging?: boolean;
    transactionsFor?: string;
}

function resolveAmountColor(amount: number): DataViewCellTextColor {
    return amount >= 0 ? 'success' : 'error';
}

function resolveAmountPrefix(amount: number) {
    return amount > 0 ? `+${amount}` : amount;
}

class TransactionsPage extends Component<TransactionsPageProps, TransactionsPageState> {

    private transactionService: TransactionService;
    private userService: UserService;
    private accountService: AccountService;

    columns: DataViewColumn[] = [{
        name: 'type',
        pipe: ucFirst
    }, {
        name: 'name',
        query: {
            controls: [
                textFilter('name')
            ]
        }
    }, {
        name: 'user.name',
        title: 'User'
    }, {
        name: 'amountType',
        pipe: fromCamelCaseToHumanCase
    }, {
        name: 'initialAmount',
        format: DataViewCellFormat.MONEY,
    }, {
        name: 'difference',
        query: {
            controls: [
                textFilter('note')
            ]
        },
        format: DataViewCellFormat.MONEY,
        pipe: resolveAmountPrefix,
        color: resolveAmountColor,
        canClick: (transaction: Transaction) => !isBlank(transaction.note),
        onClick: (transaction, context) => this.openNote(transaction, context.anchor)
    }, {
        name: 'finalAmount',
        format: DataViewCellFormat.MONEY,
    }, {
        name: 'createdAt',
        title: 'Create Date',
        pipe: v => moment(v).format('DD/MM/YYYY'),
        query: {
            controls: [
                sorting('createdAt')
            ]
        }
    }];

    private paged: DataViewPaged = {
        onChange: (offset, limit, filter?: DataFormResult) => {
            this.setState({ repaging: false });

            const { transactionsFor } = this.state;

            if (transactionsFor) {
                filter = { ...filter, accountId: transactionsFor };
            }

            return this.transactionService.getAll(offset, limit, filter).pipe(
                tap(data => {
                    this.setState({ data });
                }, () => {
                    this.setState({ data: [] });
                })
            );
        }
    };

    constructor(props: TransactionsPageProps) {
        super(props);

        const container = props.container;

        this.transactionService = container.get(TransactionService);
        this.userService = container.get(UserService);
        this.accountService = container.get(AccountService);

        this.state = {
            data: [],
            accounts: [],
            repaging: false
        }
    }

    componentDidMount() {
        this.accountService.getAll().subscribe(accounts => this.setState({ accounts }), console.error);
        this.userService.get().subscribe(user =>
            this.setState({ user, transactionsFor: user.activeAccountId }),
            console.error);
    }

    render() {

        const { data, user, accounts, transactionsFor, repaging } = this.state;
        const { classes } = this.props;

        return (<Fragment>
            <Alert severity="info" action={
                <>
                    {accounts.length > 0 && transactionsFor && <Box mr={1}>
                        <Grid container alignItems="center" spacing={1}>
                            <Grid item><label>Transactions for</label></Grid>
                            <Grid item>
                                <Box minWidth={100}>
                                    <Select
                                        fullWidth
                                        value={transactionsFor}
                                        onChange={({ target }) => {
                                            const accountId = target.value as string;
                                            this.setState({ repaging: true, transactionsFor: accountId });
                                        }}
                                    >
                                        {accounts.map(({ name, id }) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
                                    </Select>
                                </Box>
                            </Grid>
                        </Grid></Box>}
                </>

            }><div>The currently selected account is <strong>{user?.activeAccount.name}</strong></div></Alert>
            <br />
            <DataPaper>
                <DataView
                    title="Transactions"
                    data={data}
                    paged={this.paged}
                    repaging={repaging}
                    columns={this.columns} />
            </DataPaper>

            {!isBlank(this.state.note?.content) && (<Popover
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                open={!!this.state.note!.anchor}
                anchorEl={this.state.note!.anchor}
                onClose={this.closeNote.bind(this)}>
                <div className={classes.notePopover}>
                    <Typography className={classes.noteTitle} component="h2" variant="subtitle2">Note</Typography>
                    <Divider />
                    <Typography className={classes.noteContent} component="p" variant="body2">{this.state.note!.content}</Typography>
                </div>
            </Popover>)}

        </Fragment>);
    }

    closeNote() {
        this.setState({
            note: cloneWith(this.state.note, {
                open: false,
                anchor: undefined
            })
        });
    }

    openNote(transaction: Transaction, anchor: HTMLElement) {
        this.setState({
            note: {
                open: true,
                anchor,
                content: transaction.note
            }
        });
    }
}

export default withStyles(styles)(TransactionsPage);